import { Client } from '@twilio/conversations';
import { Dot, If, P, useRelativeTime } from '@fashionnovateam/nova-ui-toolkit';
import {
  ChatConversation,
  useChatContext,
  useConversationById,
  useLastMessage,
  useParticipantFromMessage,
  useUserFromParticipant,
} from '../Chat';

function CounterLatestMessageLastMessage(props: {
  conversation: ChatConversation;
}) {
  const { conversation } = props;
  const { userId } = useChatContext();

  const lastMessageProcess = useLastMessage(userId, conversation.instance);
  const participantProcess = useParticipantFromMessage(
    userId,
    lastMessageProcess.data?.instance,
  );
  const userProcess = useUserFromParticipant(
    userId,
    participantProcess.data?.instance,
  );

  const t = useRelativeTime(
    conversation.instance.lastMessage?.dateCreated ||
      conversation.instance.dateCreated ||
      0,
  );

  const textPreview =
    lastMessageProcess.data?.attributes.type === 'tiptap'
      ? lastMessageProcess.data.attributes.preview
      : lastMessageProcess.data?.body || '';

  const preview =
    textPreview.trim().length === 0
      ? `${lastMessageProcess.data?.files.length} files attached`
      : textPreview.trim();

  if (
    !(
      !lastMessageProcess.isLoading &&
      !participantProcess.isLoading &&
      !userProcess.isLoading
    )
  ) {
    return <div>Loading...</div>;
  }

  if (!lastMessageProcess.data) {
    return <div>No messages</div>;
  }

  return (
    <>
      <div className="flex flex-row gap-2">
        <P size="2xs" weight="normal" color="disabled">
          {t}
        </P>
        <If
          condition={
            conversation.instance.lastMessage?.index &&
            (!conversation.instance.lastReadMessageIndex ||
              conversation.instance.lastReadMessageIndex <
                conversation.instance.lastMessage.index)
          }
        >
          <Dot color="red" />
        </If>
      </div>
      <P size="xs" weight="medium">
        {preview.length > 49 ? `${preview.slice(0, 49)}...` : preview}
      </P>
    </>
  );
}

function CounterLatestMessageConversation(props: {
  client: Client;
  counterId: string;
}) {
  const { client, counterId } = props;
  const { userId } = useChatContext();

  const conversationProcess = useConversationById(
    userId,
    client,
    'counter',
    counterId,
  );

  if (conversationProcess.isLoading) {
    return (
      <P size="2xs" weight="normal" color="disabled">
        Loading...
      </P>
    );
  }

  if (conversationProcess.error || !conversationProcess.data) {
    const error =
      conversationProcess.error instanceof Error
        ? conversationProcess.error.message
        : 'Failed to retrieve conversation';

    if (error === 'Not Found' || error === 'Forbidden') {
      return (
        <P size="2xs" weight="normal" color="disabled">
          -
        </P>
      );
    }

    return (
      <P size="2xs" weight="normal" color="disabled">
        {error}
      </P>
    );
  }

  return (
    <CounterLatestMessageLastMessage conversation={conversationProcess.data} />
  );
}

export function CounterLatestMessage({ counterId }: { counterId: string }) {
  const { client, error } = useChatContext();

  return (
    <div className="flex flex-col h-full justify-center items-start">
      {client && !error ? (
        <CounterLatestMessageConversation
          client={client}
          counterId={counterId}
        />
      ) : (
        <P size="2xs" weight="normal" color="disabled">
          Not connected to Chat
        </P>
      )}
    </div>
  );
}
